import { IconButton } from '@mui/material';
import BaseTooltip from 'components/ui/tooltips/BaseTooltip';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const ZOOM_STEP = 1.3;
// Shundan kam siljish surish emas, bosish deb hisoblanadi — aks holda
// poligonni bosmoqchi bo'lganda qo'l biroz qimirlasa klik yo'qoladi
const DRAG_THRESHOLD = 4;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Vizual ko'rinish uchun kattalashtirish va sichqoncha bilan surish.
 *
 * Ichidagi bolalar `transform: translate() scale()` bilan siljitiladi,
 * boshqaruv tugmalari esa transformdan tashqarida turadi (ular ham
 * kattalashib ketmasligi uchun). `onTransform` har safar ko'rinish
 * o'zgarganda chaqiriladi — HomesView xonadon raqamlari yorliqlarini
 * shu signal bo'yicha qayta joylaydi.
 */
const VisualZoomBox = forwardRef(function VisualZoomBox(
  { className = '', onTransform = () => {}, children },
  ref,
) {
  const { t } = useTranslation();
  const viewportRef = useRef(null);
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  // Surish paytidagi holat — har bir mousemove'da qayta render qilmaslik uchun
  const panRef = useRef(null);
  // Surishdan keyingi klikni to'xtatish kerakmi
  const movedRef = useRef(false);

  // Plan ko'rinish chegarasidan tashqariga chiqib ketmasligi uchun:
  // scale=1 da surish umuman bo'lmaydi, kattalashtirilganda esa
  // faqat ko'rinishni to'ldirgan holda suriladi
  const clampOffset = useCallback((next) => {
    const el = viewportRef.current;
    if (!el) return next;

    const { width, height } = el.getBoundingClientRect();

    return {
      scale: next.scale,
      x: clamp(next.x, width * (1 - next.scale), 0),
      y: clamp(next.y, height * (1 - next.scale), 0),
    };
  }, []);

  // Tugma bilan kattalashtirish — markazni ushlab turgan holda
  const zoomByButton = (factor) => {
    const el = viewportRef.current;
    if (!el) return;

    const { width, height } = el.getBoundingClientRect();

    setTransform((prev) =>
      zoomAtFrom(prev, prev.scale * factor, {
        x: width / 2,
        y: height / 2,
        width,
        height,
      }),
    );
  };

  const resetTransform = () => setTransform({ scale: 1, x: 0, y: 0 });

  /**
   * Berilgan elementni (masalan bosilgan xonadon poligoni) ko'rinish
   * markaziga olib kelib yaqinlashtiradi.
   *
   * Element o'lchami EKRAN o'lchami — ya'ni hozirgi masshtab allaqachon
   * ichida. Shuning uchun yangi masshtab eskisiga nisbatan hisoblanadi.
   */
  const zoomToElement = useCallback(
    // fill — poligon ko'rinishning qancha ulushini egallasin.
    // max  — bosish bilan yetib boradigan eng katta masshtab. Qo'lda
    //        (g'ildirak/tugma) MAX_SCALE gacha yaqinlashtirish mumkin,
    //        bosishda esa bundan kamroq — aks holda juda yaqin ketadi.
    (el, { fill = 0.3, max = 3.5 } = {}) => {
      const viewport = viewportRef.current;
      if (!el || !viewport) return;

      const v = viewport.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height || !v.width || !v.height) return;

      setTransform((prev) => {
        // Poligon ko'rinishning `fill` ulushini egallasin
        const nextScale = clamp(
          Math.min((v.width * fill) / r.width, (v.height * fill) / r.height) *
            prev.scale,
          MIN_SCALE,
          Math.min(max, MAX_SCALE),
        );

        // Poligon markazi — transformdan OLDINGI (kontent) koordinatada
        const cx = (r.left + r.width / 2 - v.left - prev.x) / prev.scale;
        const cy = (r.top + r.height / 2 - v.top - prev.y) / prev.scale;

        return {
          scale: nextScale,
          x: clamp(v.width / 2 - cx * nextScale, v.width * (1 - nextScale), 0),
          y: clamp(
            v.height / 2 - cy * nextScale,
            v.height * (1 - nextScale),
            0,
          ),
        };
      });
    },
    [],
  );

  useImperativeHandle(ref, () => ({ zoomToElement, reset: resetTransform }), [
    zoomToElement,
  ]);

  // G'ildirak: React'da onWheel passiv tinglovchi bo'lgani uchun
  // preventDefault ishlamaydi — qo'lda, passive: false bilan ulanadi
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    const handleWheel = (event) => {
      event.preventDefault();
      const rect = el.getBoundingClientRect();
      const factor = event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;

      setTransform((prev) =>
        zoomAtFrom(prev, prev.scale * factor, {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
          width: rect.width,
          height: rect.height,
        }),
      );
    };

    el.addEventListener('wheel', handleWheel, { passive: false });

    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  const handlePointerDown = (event) => {
    // Faqat chap tugma bilan suriladi
    if (event.button !== 0) return;

    panRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: transform.x,
      originY: transform.y,
    };
    movedRef.current = false;
  };

  const handlePointerMove = (event) => {
    const pan = panRef.current;
    if (!pan) return;

    const dx = event.clientX - pan.startX;
    const dy = event.clientY - pan.startY;

    if (!movedRef.current) {
      if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD)
        return;

      movedRef.current = true;
      setIsPanning(true);
      // Surish boshlangach poligonlar hover/klikni ushlab qolmasin
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }

    setTransform((prev) =>
      clampOffset({
        scale: prev.scale,
        x: pan.originX + dx,
        y: pan.originY + dy,
      }),
    );
  };

  const endPan = (event) => {
    if (!panRef.current) return;

    panRef.current = null;
    setIsPanning(false);
    event?.currentTarget?.releasePointerCapture?.(event.pointerId);
  };

  // Surib bo'lgach keladigan klik poligonni ochib yubormasin
  const handleClickCapture = (event) => {
    if (!movedRef.current) return;

    movedRef.current = false;
    event.stopPropagation();
    event.preventDefault();
  };

  useEffect(() => {
    onTransform(transform);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transform]);

  const isZoomed = transform.scale > MIN_SCALE;

  return (
    <div
      ref={viewportRef}
      className={`visual-zoom-viewport${isPanning ? ' is-panning' : ''}${
        isZoomed ? ' is-zoomed' : ''
      }${className ? ` ${className}` : ''}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endPan}
      onPointerLeave={endPan}
      onPointerCancel={endPan}
      onClickCapture={handleClickCapture}
      onDragStart={(event) => event.preventDefault()}
    >
      <div
        className='visual-zoom-content'
        style={{
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
        }}
      >
        {children}
      </div>

      <div className='visual-zoom-controls'>
        <BaseTooltip
          title={t('shaxmatka.zoomIn', 'Kattalashtirish')}
          arrow
          placement='left'
        >
          <span>
            <IconButton
              size='small'
              color='primary'
              disabled={transform.scale >= MAX_SCALE}
              onClick={() => zoomByButton(ZOOM_STEP)}
            >
              <i className='bi bi-zoom-in' />
            </IconButton>
          </span>
        </BaseTooltip>

        <BaseTooltip
          title={t('shaxmatka.zoomOut', 'Kichiklashtirish')}
          arrow
          placement='left'
        >
          <span>
            <IconButton
              size='small'
              color='primary'
              disabled={transform.scale <= MIN_SCALE}
              onClick={() => zoomByButton(1 / ZOOM_STEP)}
            >
              <i className='bi bi-zoom-out' />
            </IconButton>
          </span>
        </BaseTooltip>

        <BaseTooltip
          title={t('shaxmatka.zoomReset', 'Asl holatga qaytarish')}
          arrow
          placement='left'
        >
          <span>
            <IconButton
              size='small'
              color='primary'
              disabled={!isZoomed}
              onClick={resetTransform}
            >
              <i className='bi bi-arrows-angle-contract' />
            </IconButton>
          </span>
        </BaseTooltip>
      </div>
    </div>
  );
});

// setTransform ichida chaqirish uchun — oldingi holatdan yangisini hisoblaydi
function zoomAtFrom(prev, nextScaleRaw, point) {
  const nextScale = clamp(nextScaleRaw, MIN_SCALE, MAX_SCALE);
  if (nextScale === prev.scale) return prev;

  const ratio = nextScale / prev.scale;
  const x = point.x - (point.x - prev.x) * ratio;
  const y = point.y - (point.y - prev.y) * ratio;

  return {
    scale: nextScale,
    x: clamp(x, point.width * (1 - nextScale), 0),
    y: clamp(y, point.height * (1 - nextScale), 0),
  };
}

export default memo(VisualZoomBox);
